/* Enhanced ChatContext.jsx - With robust rate limit handling and automatic fallbacks */
import React, { 
  createContext, 
  useState, 
  useContext, 
  useEffect, 
  useCallback, 
  useMemo,
  useRef,
  useReducer
} from 'react';
import { 
  sendMessage, 
  sendMessageQueued,
  modelConstants, 
  getBestAvailableModel,
  getModelStatus,
  rateLimitTracker 
} from '../services/openRouterService';
import { useAuth } from './AuthContext';
import { 
  doc, 
  getDoc, 
  collection, 
  addDoc, 
  serverTimestamp, 
  query, 
  orderBy, 
  onSnapshot,
  updateDoc,
  writeBatch,
  enableNetwork,
  disableNetwork,
  Timestamp
} from 'firebase/firestore';
import { db } from '../services/firebase';

const ChatContext = createContext();

// Action types for reducer
const CHAT_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_MESSAGES: 'SET_MESSAGES',
  ADD_MESSAGE: 'ADD_MESSAGE',
  UPDATE_MESSAGE: 'UPDATE_MESSAGE',
  DELETE_MESSAGE: 'DELETE_MESSAGE',
  SET_CONVERSATIONS: 'SET_CONVERSATIONS',
  ADD_CONVERSATION: 'ADD_CONVERSATION',
  UPDATE_CONVERSATION: 'UPDATE_CONVERSATION',
  DELETE_CONVERSATION: 'DELETE_CONVERSATION',
  SET_CURRENT_CONVERSATION: 'SET_CURRENT_CONVERSATION',
  SET_STREAMING: 'SET_STREAMING',
  SET_STREAMING_MESSAGE: 'SET_STREAMING_MESSAGE',
  SET_API_KEY: 'SET_API_KEY',
  SET_SELECTED_MODEL: 'SET_SELECTED_MODEL',
  SET_EXTENDED_THINKING: 'SET_EXTENDED_THINKING',
  SET_NETWORK_STATUS: 'SET_NETWORK_STATUS',
  SET_WEB_SEARCHING: 'SET_WEB_SEARCHING',
  SET_TYPING_STATUS: 'SET_TYPING_STATUS',
  SET_MODEL_STATUS: 'SET_MODEL_STATUS',
  SET_RETRY_INFO: 'SET_RETRY_INFO'
};

// Initial state
const initialState = {
  messages: [],
  conversations: [],
  currentConversation: null,
  loading: false,
  error: null,
  apiKey: '',
  selectedModel: modelConstants.expertModels.general,
  extendedThinking: false,
  streamingMessage: '',
  isStreaming: false,
  networkStatus: 'online',
  isWebSearching: false,
  typingStatus: null,
  modelStatus: {},
  retryInfo: null
};

// Enhanced reducer with better error handling
const chatReducer = (state, action) => {
  try {
    switch (action.type) {
      case CHAT_ACTIONS.SET_LOADING:
        return { ...state, loading: Boolean(action.payload) };
      
      case CHAT_ACTIONS.SET_ERROR:
        return { 
          ...state, 
          error: action.payload || null, 
          loading: false,
          isStreaming: false,
          isWebSearching: false,
          typingStatus: null,
          retryInfo: null
        };
      
      case CHAT_ACTIONS.CLEAR_ERROR:
        return { ...state, error: null, retryInfo: null };
      
      case CHAT_ACTIONS.SET_MESSAGES:
        return { 
          ...state, 
          messages: Array.isArray(action.payload) ? action.payload : [] 
        };
      
      case CHAT_ACTIONS.ADD_MESSAGE:
        if (!action.payload) return state;
        return { 
          ...state, 
          messages: [...state.messages, action.payload] 
        };
      
      case CHAT_ACTIONS.UPDATE_MESSAGE:
        if (!action.payload || !action.payload.id) return state;
        return {
          ...state,
          messages: state.messages.map(msg =>
            msg.id === action.payload.id ? { ...msg, ...action.payload.updates } : msg
          )
        };
      
      case CHAT_ACTIONS.DELETE_MESSAGE:
        return {
          ...state,
          messages: state.messages.filter(msg => msg.id !== action.payload)
        };
      
      case CHAT_ACTIONS.SET_CONVERSATIONS:
        return { 
          ...state, 
          conversations: Array.isArray(action.payload) ? action.payload : [] 
        };
      
      case CHAT_ACTIONS.ADD_CONVERSATION:
        if (!action.payload) return state;
        return { 
          ...state, 
          conversations: [action.payload, ...state.conversations] 
        };
      
      case CHAT_ACTIONS.UPDATE_CONVERSATION:
        if (!action.payload || !action.payload.id) return state;
        return {
          ...state,
          conversations: state.conversations.map(conv =>
            conv.id === action.payload.id ? { ...conv, ...action.payload.updates } : conv
          )
        };
      
      case CHAT_ACTIONS.DELETE_CONVERSATION:
        return {
          ...state,
          conversations: state.conversations.filter(conv => conv.id !== action.payload),
          currentConversation: state.currentConversation === action.payload ? null : state.currentConversation
        };
      
      case CHAT_ACTIONS.SET_CURRENT_CONVERSATION:
        return { ...state, currentConversation: action.payload };
      
      case CHAT_ACTIONS.SET_STREAMING:
        return { 
          ...state, 
          isStreaming: Boolean(action.payload),
          typingStatus: action.payload ? 'typing' : null
        };
      
      case CHAT_ACTIONS.SET_STREAMING_MESSAGE:
        return { ...state, streamingMessage: action.payload || '' };
      
      case CHAT_ACTIONS.SET_API_KEY:
        return { ...state, apiKey: action.payload || '' };
      
      case CHAT_ACTIONS.SET_SELECTED_MODEL:
        return { ...state, selectedModel: action.payload || modelConstants.expertModels.general };
      
      case CHAT_ACTIONS.SET_EXTENDED_THINKING:
        return { ...state, extendedThinking: Boolean(action.payload) };
      
      case CHAT_ACTIONS.SET_NETWORK_STATUS:
        return { ...state, networkStatus: action.payload || 'online' };
      
      case CHAT_ACTIONS.SET_WEB_SEARCHING:
        return { 
          ...state, 
          isWebSearching: Boolean(action.payload),
          typingStatus: action.payload ? 'searching' : null
        };
      
      case CHAT_ACTIONS.SET_TYPING_STATUS:
        return { ...state, typingStatus: action.payload };
      
      case CHAT_ACTIONS.SET_MODEL_STATUS:
        return { 
          ...state, 
          modelStatus: { ...state.modelStatus, ...action.payload } 
        };
      
      case CHAT_ACTIONS.SET_RETRY_INFO:
        return { ...state, retryInfo: action.payload };
      
      default:
        console.warn('Unknown action type:', action.type);
        return state;
    }
  } catch (error) {
    console.error('Error in chatReducer:', error);
    return state;
  }
};

// Safe timestamp utilities
const safeTimestamp = {
  now: () => {
    try {
      return serverTimestamp();
    } catch (error) {
      console.warn('Server timestamp failed, using client timestamp:', error);
      return Timestamp.now();
    }
  },
  
  fromDate: (date) => {
    try {
      return Timestamp.fromDate(date instanceof Date ? date : new Date(date));
    } catch (error) {
      console.warn('Failed to create timestamp from date:', error);
      return Timestamp.now();
    }
  },
  
  toDate: (timestamp) => {
    try {
      if (!timestamp) return new Date();
      
      if (timestamp && typeof timestamp.seconds === 'number') {
        return new Date(timestamp.seconds * 1000);
      }
      
      if (timestamp && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
      }
      
      if (timestamp instanceof Date) {
        return timestamp;
      }
      
      if (typeof timestamp === 'number' || typeof timestamp === 'string') {
        const date = new Date(timestamp);
        return isNaN(date.getTime()) ? new Date() : date;
      }
      
      return new Date();
    } catch (error) {
      console.warn('Failed to convert timestamp to date:', error);
      return new Date();
    }
  }
};

// Enhanced local storage with error handling
const useOptimizedLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = useCallback((value) => {
    try {
      setStoredValue(value);
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error saving to localStorage key "${key}":`, error);
    }
  }, [key]);

  return [storedValue, setValue];
};

// Enhanced network status hook
const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      try {
        enableNetwork(db);
      } catch (error) {
        console.warn('Failed to enable Firestore network:', error);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      try {
        disableNetwork(db);
      } catch (error) {
        console.warn('Failed to disable Firestore network:', error);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

// Enhanced message queue
class MessageQueue {
  constructor() {
    this.queue = this.loadQueue();
  }

  loadQueue() {
    try {
      const stored = localStorage.getItem('messageQueue');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Failed to load message queue:', error);
      return [];
    }
  }

  add(message) {
    try {
      this.queue.push({ 
        ...message, 
        queueTimestamp: Date.now(),
        id: message.id || `queue_${Date.now()}`
      });
      this.save();
    } catch (error) {
      console.error('Failed to add message to queue:', error);
    }
  }

  getAll() {
    return this.queue;
  }

  remove(id) {
    try {
      this.queue = this.queue.filter(msg => msg.id !== id);
      this.save();
    } catch (error) {
      console.error('Failed to remove message from queue:', error);
    }
  }

  clear() {
    try {
      this.queue = [];
      this.save();
    } catch (error) {
      console.error('Failed to clear message queue:', error);
    }
  }

  save() {
    try {
      localStorage.setItem('messageQueue', JSON.stringify(this.queue));
    } catch (error) {
      console.error('Failed to save message queue:', error);
    }
  }
}

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const isOnline = useNetworkStatus();
  const messageQueueRef = useRef(new MessageQueue());

  // Local storage for preferences
  const [apiKeyStorage, setApiKeyStorage] = useOptimizedLocalStorage('openrouter_api_key', '');
  const [selectedModelStorage, setSelectedModelStorage] = useOptimizedLocalStorage('selected_model', modelConstants.expertModels.general);
  const [extendedThinkingStorage, setExtendedThinkingStorage] = useOptimizedLocalStorage('extended_thinking', false);

  // Initialize state from local storage
  useEffect(() => {
    dispatch({ type: CHAT_ACTIONS.SET_API_KEY, payload: apiKeyStorage });
    dispatch({ type: CHAT_ACTIONS.SET_SELECTED_MODEL, payload: selectedModelStorage });
    dispatch({ type: CHAT_ACTIONS.SET_EXTENDED_THINKING, payload: extendedThinkingStorage });
  }, [apiKeyStorage, selectedModelStorage, extendedThinkingStorage]);

  // Update network status
  useEffect(() => {
    dispatch({ type: CHAT_ACTIONS.SET_NETWORK_STATUS, payload: isOnline ? 'online' : 'offline' });
  }, [isOnline]);

  // Update model status periodically
  useEffect(() => {
    const updateModelStatus = () => {
      const allModels = Object.keys(modelConstants.modelInfo);
      const statusUpdate = {};
      
      allModels.forEach(modelId => {
        statusUpdate[modelId] = getModelStatus(modelId);
      });
      
      dispatch({ type: CHAT_ACTIONS.SET_MODEL_STATUS, payload: statusUpdate });
    };

    updateModelStatus();
    const interval = setInterval(updateModelStatus, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Safe data processing for Firestore documents
  const processFirestoreDocument = useCallback((doc) => {
    try {
      const data = doc.data();
      if (!data) return null;

      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt ? safeTimestamp.toDate(data.createdAt) : new Date(),
        updatedAt: data.updatedAt ? safeTimestamp.toDate(data.updatedAt) : new Date(),
        timestamp: data.timestamp ? safeTimestamp.toDate(data.timestamp) : new Date()
      };
    } catch (error) {
      console.error('Error processing Firestore document:', error);
      return null;
    }
  }, []);

  // Create conversation function
  const createConversation = useCallback(async (title = 'New Chat') => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      const conversationRef = collection(db, `users/${currentUser.uid}/conversations`);
      const conversationData = {
        title: title || 'New Chat',
        model: state.selectedModel || modelConstants.expertModels.general,
        createdAt: safeTimestamp.now(),
        updatedAt: safeTimestamp.now(),
      };

      const newConversation = await addDoc(conversationRef, conversationData);
      dispatch({ type: CHAT_ACTIONS.SET_CURRENT_CONVERSATION, payload: newConversation.id });
      return newConversation.id;
    } catch (error) {
      console.error("Error creating conversation:", error);
      dispatch({ type: CHAT_ACTIONS.SET_ERROR, payload: "Failed to create new conversation" });
      throw error;
    }
  }, [currentUser, state.selectedModel]);

  // Load conversations from Firestore
  useEffect(() => {
    if (!currentUser) {
      dispatch({ type: CHAT_ACTIONS.SET_CONVERSATIONS, payload: [] });
      return;
    }

    try {
      const conversationsRef = collection(db, `users/${currentUser.uid}/conversations`);
      const q = query(conversationsRef, orderBy('updatedAt', 'desc'));
      
      const unsubscribe = onSnapshot(q, 
        (snapshot) => {
          try {
            const conversationList = snapshot.docs
              .map(doc => processFirestoreDocument(doc))
              .filter(conv => conv !== null);
            
            dispatch({ type: CHAT_ACTIONS.SET_CONVERSATIONS, payload: conversationList });
            
            if (!state.currentConversation && conversationList.length > 0) {
              dispatch({ type: CHAT_ACTIONS.SET_CURRENT_CONVERSATION, payload: conversationList[0].id });
            }
          } catch (error) {
            console.error("Error processing conversations snapshot:", error);
            dispatch({ type: CHAT_ACTIONS.SET_ERROR, payload: "Failed to process conversations" });
          }
        },
        (error) => {
          console.error("Error loading conversations:", error);
          dispatch({ type: CHAT_ACTIONS.SET_ERROR, payload: "Failed to load conversations" });
        }
      );

      return unsubscribe;
    } catch (error) {
      console.error("Error setting up conversations listener:", error);
      dispatch({ type: CHAT_ACTIONS.SET_ERROR, payload: "Failed to initialize conversations" });
    }
  }, [currentUser, processFirestoreDocument, state.currentConversation]);

  // Load messages for current conversation
  useEffect(() => {
    if (!currentUser || !state.currentConversation) {
      dispatch({ type: CHAT_ACTIONS.SET_MESSAGES, payload: [] });
      return;
    }

    try {
      const messagesRef = collection(db, `users/${currentUser.uid}/conversations/${state.currentConversation}/messages`);
      const q = query(messagesRef, orderBy('timestamp', 'asc'));
      
      const unsubscribe = onSnapshot(q,
        (snapshot) => {
          try {
            const messageList = snapshot.docs
              .map(doc => processFirestoreDocument(doc))
              .filter(msg => msg !== null);
            
            dispatch({ type: CHAT_ACTIONS.SET_MESSAGES, payload: messageList });
          } catch (error) {
            console.error("Error processing messages snapshot:", error);
            dispatch({ type: CHAT_ACTIONS.SET_ERROR, payload: "Failed to process messages" });
          }
        },
        (error) => {
          console.error("Error loading messages:", error);
          dispatch({ type: CHAT_ACTIONS.SET_ERROR, payload: "Failed to load messages" });
        }
      );

      return unsubscribe;
    } catch (error) {
      console.error("Error setting up messages listener:", error);
      dispatch({ type: CHAT_ACTIONS.SET_ERROR, payload: "Failed to initialize messages" });
    }
  }, [currentUser, state.currentConversation, processFirestoreDocument]);

  // Enhanced message sending to Firestore
  const sendMessageToFirestore = useCallback(async (messageData) => {
    if (!currentUser || !state.currentConversation) {
      throw new Error('No user or conversation selected');
    }

    try {
      const messagesRef = collection(db, `users/${currentUser.uid}/conversations/${state.currentConversation}/messages`);
      
      const safeMessageData = {
        ...messageData,
        timestamp: safeTimestamp.now(),
        createdAt: safeTimestamp.now(),
        role: messageData.role || 'user',
        content: messageData.content || '',
        id: messageData.id || `msg_${Date.now()}`
      };

      return await addDoc(messagesRef, safeMessageData);
    } catch (error) {
      console.error('Error sending message to Firestore:', error);
      throw error;
    }
  }, [currentUser, state.currentConversation]);

  // Process offline message queue
  useEffect(() => {
    if (isOnline && currentUser) {
      const processQueue = async () => {
        try {
          const queue = messageQueueRef.current.getAll();
          for (const queuedMessage of queue) {
            try {
              await sendMessageToFirestore(queuedMessage);
              messageQueueRef.current.remove(queuedMessage.id);
            } catch (error) {
              console.error('Failed to process queued message:', error);
            }
          }
        } catch (error) {
          console.error('Error processing message queue:', error);
        }
      };
      processQueue();
    }
  }, [isOnline, currentUser, sendMessageToFirestore]);

  // Enhanced streaming with better error handling
  const processStreamResponse = useCallback(async (response) => {
    if (!response || !response.body) {
      throw new Error('Invalid response for streaming');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let accumulatedData = "";
    let assistantMessage = "";
    
    dispatch({ type: CHAT_ACTIONS.SET_STREAMING, payload: true });
    dispatch({ type: CHAT_ACTIONS.SET_TYPING_STATUS, payload: 'typing' });
    
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value);
        accumulatedData += chunk;
        const lines = accumulatedData.split('\n');
        accumulatedData = lines.pop() || "";
        
        for (const line of lines) {
          if (!line.trim() || line.trim() === "data: [DONE]") continue;
          
          try {
            const dataMatch = line.match(/^data: (.+)$/);
            if (!dataMatch) continue;
            
            const data = JSON.parse(dataMatch[1]);
            const content = data.choices?.[0]?.delta?.content || "";
            
            if (content) {
              assistantMessage += content;
              dispatch({ type: CHAT_ACTIONS.SET_STREAMING_MESSAGE, payload: assistantMessage });
            }
          } catch (error) {
            console.warn("Error parsing stream chunk:", error);
          }
        }
      }
    } catch (error) {
      console.error("Error reading stream:", error);
      throw error;
    } finally {
      dispatch({ type: CHAT_ACTIONS.SET_STREAMING, payload: false });
      dispatch({ type: CHAT_ACTIONS.SET_STREAMING_MESSAGE, payload: '' });
      dispatch({ type: CHAT_ACTIONS.SET_TYPING_STATUS, payload: null });
    }
    
    return assistantMessage;
  }, []);

  // Enhanced message sending with comprehensive error handling and fallbacks
  const sendChatMessage = useCallback(async (userMessage, attachments = [], tokenLimit = 2000) => {
    if (!state.apiKey) {
      dispatch({ type: CHAT_ACTIONS.SET_ERROR, payload: "OpenRouter API key is required" });
      throw new Error("OpenRouter API key is required");
    }

    if (!state.currentConversation) {
      try {
        await createConversation();
      } catch (error) {
        console.error('Failed to create conversation:', error);
        dispatch({ type: CHAT_ACTIONS.SET_ERROR, payload: "Failed to create conversation" });
        throw error;
      }
    }

    dispatch({ type: CHAT_ACTIONS.CLEAR_ERROR });
    dispatch({ type: CHAT_ACTIONS.SET_TYPING_STATUS, payload: 'thinking' });

    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newUserMessage = {
      id: messageId,
      role: 'user',
      content: userMessage || '',
      timestamp: safeTimestamp.now(),
      attachments: attachments || [],
      createdAt: safeTimestamp.now()
    };

    let currentModel = state.selectedModel;
    let usedFallback = false;

    try {
      dispatch({ type: CHAT_ACTIONS.ADD_MESSAGE, payload: newUserMessage });

      if (isOnline) {
        try {
          await sendMessageToFirestore(newUserMessage);
        } catch (firestoreError) {
          console.warn('Failed to save to Firestore, queuing message:', firestoreError);
          messageQueueRef.current.add(newUserMessage);
        }
      } else {
        messageQueueRef.current.add(newUserMessage);
      }

      const messagesForApi = state.messages.map(msg => ({
        role: msg.role || 'user',
        content: msg.content || '',
        ...(msg.attachments?.length > 0 && {
          images: msg.attachments.map(att => `data:${att.type};base64,${att.data}`)
        })
      }));

      messagesForApi.push({
        role: 'user',
        content: userMessage || '',
        ...(attachments.length > 0 && {
          images: attachments.map(att => `data:${att.type};base64,${att.data}`)
        })
      });

      dispatch({ type: CHAT_ACTIONS.SET_LOADING, payload: true });

      // Determine category for better model selection
      const category = attachments.length > 0 ? 'vision' : 'general';
      
      // Get the best available model if the selected one is rate limited
      const bestAvailableModel = getBestAvailableModel(category);
      if (bestAvailableModel !== currentModel) {
        currentModel = bestAvailableModel;
        usedFallback = true;
        console.log(`Using fallback model: ${currentModel}`);
      }

      const response = await sendMessage(
        state.apiKey, 
        currentModel, 
        messagesForApi, 
        state.extendedThinking,
        tokenLimit,
        {
          category,
          fallbackEnabled: true,
          onModelChange: (newModel, reason) => {
            console.log(`Model changed to ${newModel} due to ${reason}`);
            if (reason === 'fallback') {
              dispatch({ 
                type: CHAT_ACTIONS.SET_RETRY_INFO, 
                payload: { 
                  type: 'model_fallback', 
                  originalModel: state.selectedModel,
                  currentModel: newModel,
                  reason: 'Rate limit or availability issue'
                }
              });
            }
          },
          onRetry: (retryCount, model) => {
            console.log(`Retry ${retryCount} for model ${model}`);
            dispatch({ 
              type: CHAT_ACTIONS.SET_RETRY_INFO, 
              payload: { 
                type: 'retry', 
                retryCount,
                model,
                message: `Retrying... (attempt ${retryCount})`
              }
            });
          }
        }
      );

      const assistantResponse = await processStreamResponse(response);

      const assistantMessageId = `msg_${Date.now()}_assistant_${Math.random().toString(36).substr(2, 9)}`;
      const newAssistantMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: assistantResponse || '',
        timestamp: safeTimestamp.now(),
        model: currentModel,
        createdAt: safeTimestamp.now(),
        usedFallback
      };

      dispatch({ type: CHAT_ACTIONS.ADD_MESSAGE, payload: newAssistantMessage });

      if (isOnline) {
        try {
          await sendMessageToFirestore(newAssistantMessage);

          if (currentUser && state.currentConversation) {
            try {
              const conversationRef = doc(db, `users/${currentUser.uid}/conversations/${state.currentConversation}`);
              const conversationDoc = await getDoc(conversationRef);
              
              if (conversationDoc.exists()) {
                const updateData = { 
                  updatedAt: safeTimestamp.now(),
                  lastModel: currentModel
                };
                
                const conversationData = conversationDoc.data();
                if (state.messages.length === 0 && (!conversationData.title || conversationData.title === 'New Chat')) {
                  updateData.title = userMessage.length > 30 
                    ? userMessage.substring(0, 30) + '...' 
                    : userMessage;
                }
                
                await updateDoc(conversationRef, updateData);
              }
            } catch (updateError) {
              console.warn('Failed to update conversation metadata:', updateError);
            }
          }
        } catch (firestoreError) {
          console.warn('Failed to save assistant message to Firestore, queuing:', firestoreError);
          messageQueueRef.current.add(newAssistantMessage);
        }
      } else {
        messageQueueRef.current.add(newAssistantMessage);
      }

      // Clear retry info on success
      dispatch({ type: CHAT_ACTIONS.SET_RETRY_INFO, payload: null });

      return assistantResponse;
    } catch (error) {
      console.error("Error sending message:", error);
      
      let errorMessage = "Failed to send message";
      
      if (error.message.includes('Rate limit exceeded')) {
        errorMessage = "Rate limit reached. The app will automatically try other available models.";
      } else if (error.message.includes('quota exceeded')) {
        errorMessage = "API quota exceeded. Please check your OpenRouter account.";
      } else if (error.message.includes('API key')) {
        errorMessage = "Invalid API key. Please check your OpenRouter API key.";
      } else {
        errorMessage = `Failed to send message: ${error.message}`;
      }
      
      dispatch({ type: CHAT_ACTIONS.SET_ERROR, payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: CHAT_ACTIONS.SET_LOADING, payload: false });
      dispatch({ type: CHAT_ACTIONS.SET_TYPING_STATUS, payload: null });
      dispatch({ type: CHAT_ACTIONS.SET_RETRY_INFO, payload: null });
    }
  }, [
    state.apiKey, 
    state.selectedModel, 
    state.extendedThinking, 
    state.currentConversation, 
    state.messages,
    isOnline,
    sendMessageToFirestore,
    processStreamResponse,
    currentUser,
    createConversation
  ]);

  // Delete conversation with enhanced error handling
  const deleteConversation = useCallback(async (conversationId) => {
    if (!currentUser || !conversationId) {
      throw new Error('Invalid parameters for conversation deletion');
    }

    try {
      const batch = writeBatch(db);
      
      const conversationRef = doc(db, `users/${currentUser.uid}/conversations/${conversationId}`);
      batch.delete(conversationRef);

      await batch.commit();

      dispatch({ type: CHAT_ACTIONS.DELETE_CONVERSATION, payload: conversationId });

      if (state.currentConversation === conversationId) {
        const otherConversation = state.conversations.find(c => c.id !== conversationId);
        dispatch({ 
          type: CHAT_ACTIONS.SET_CURRENT_CONVERSATION, 
          payload: otherConversation?.id || null 
        });
      }
    } catch (error) {
      console.error("Error deleting conversation:", error);
      dispatch({ type: CHAT_ACTIONS.SET_ERROR, payload: "Failed to delete conversation" });
      throw error;
    }
  }, [currentUser, state.currentConversation, state.conversations]);

  // Optimized setters with local storage sync
  const setApiKey = useCallback((key) => {
    setApiKeyStorage(key || '');
    dispatch({ type: CHAT_ACTIONS.SET_API_KEY, payload: key || '' });
  }, [setApiKeyStorage]);

  const setSelectedModel = useCallback((model) => {
    const safeModel = model || modelConstants.expertModels.general;
    setSelectedModelStorage(safeModel);
    dispatch({ type: CHAT_ACTIONS.SET_SELECTED_MODEL, payload: safeModel });
  }, [setSelectedModelStorage]);

  const setExtendedThinking = useCallback((thinking) => {
    const safeThinking = Boolean(thinking);
    setExtendedThinkingStorage(safeThinking);
    dispatch({ type: CHAT_ACTIONS.SET_EXTENDED_THINKING, payload: safeThinking });
  }, [setExtendedThinkingStorage]);

  const setCurrentConversation = useCallback((conversationId) => {
    dispatch({ type: CHAT_ACTIONS.SET_CURRENT_CONVERSATION, payload: conversationId });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: CHAT_ACTIONS.CLEAR_ERROR });
  }, []);

  const setWebSearching = useCallback((searching) => {
    dispatch({ type: CHAT_ACTIONS.SET_WEB_SEARCHING, payload: searching });
  }, []);

  const setTypingStatus = useCallback((status) => {
    dispatch({ type: CHAT_ACTIONS.SET_TYPING_STATUS, payload: status });
  }, []);

  // Get available models with status
  const getAvailableModelsWithStatus = useCallback(() => {
    return Object.keys(modelConstants.modelInfo).map(modelId => ({
      id: modelId,
      ...modelConstants.modelInfo[modelId],
      status: state.modelStatus[modelId] || { isRateLimited: false, nextAvailable: 0 }
    }));
  }, [state.modelStatus]);

  // Memoized context value with safe defaults
  const contextValue = useMemo(() => ({
    // State
    messages: Array.isArray(state.messages) ? state.messages : [],
    conversations: Array.isArray(state.conversations) ? state.conversations : [],
    currentConversation: state.currentConversation,
    loading: Boolean(state.loading),
    error: state.error,
    apiKey: state.apiKey || '',
    selectedModel: state.selectedModel || modelConstants.expertModels.general,
    extendedThinking: Boolean(state.extendedThinking),
    streamingMessage: state.streamingMessage || '',
    isStreaming: Boolean(state.isStreaming),
    networkStatus: state.networkStatus || 'online',
    isWebSearching: Boolean(state.isWebSearching),
    typingStatus: state.typingStatus,
    modelStatus: state.modelStatus || {},
    retryInfo: state.retryInfo,

    // Actions
    sendMessage: sendChatMessage,
    createConversation,
    deleteConversation,
    setApiKey,
    setSelectedModel,
    setExtendedThinking,
    setCurrentConversation,
    clearError,
    setWebSearching,
    setTypingStatus,

    // Utils
    isOnline,
    queuedMessages: messageQueueRef.current.getAll(),
    getAvailableModelsWithStatus,
    getBestAvailableModel,
    modelConstants
  }), [
    state,
    sendChatMessage,
    createConversation,
    deleteConversation,
    setApiKey,
    setSelectedModel,
    setExtendedThinking,
    setCurrentConversation,
    clearError,
    setWebSearching,
    setTypingStatus,
    isOnline,
    getAvailableModelsWithStatus
  ]);

  return (
    <ChatContext.Provider value={contextValue}>
      {children}
    </ChatContext.Provider>
  );
};

export default ChatProvider;